import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../services/api';
import type { PenkiTransaction } from '../types';
import { useUser } from '../contexts/UserContext';
import {
  formatMonthGroup,
  formatPenki,
  formatPenkiTransactionDate,
  monthKey,
} from '../utils/penki';
import './PenkiBalancePage.css';

interface MonthGroup {
  key: string;
  items: PenkiTransaction[];
}

function groupByMonth(transactions: PenkiTransaction[]): MonthGroup[] {
  const map = new Map<string, PenkiTransaction[]>();
  for (const tx of transactions) {
    const key = monthKey(tx.date);
    const bucket = map.get(key);
    if (bucket) bucket.push(tx);
    else map.set(key, [tx]);
  }
  return [...map.entries()]
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([key, items]) => ({ key, items }));
}

export const PenkiBalancePage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const [transactions, setTransactions] = useState<PenkiTransaction[]>([]);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const load = useCallback(async (nextPage: number, append: boolean) => {
    try {
      if (append) setLoadingMore(true);
      else setLoading(true);
      const response = await apiClient.getPenkiTransactions(nextPage);
      setLastPage(response.pagination.last_page);
      setPage(response.pagination.current_page);
      setTransactions((prev) => (append ? [...prev, ...response.data] : response.data));
    } catch (error) {
      console.error('Failed to load penki transactions', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    void load(1, false);
  }, [load]);

  const balance = user?.penki_balance ?? 0;
  const groups = groupByMonth(transactions);

  return (
    <div className="page-container penki-balance-page">
      <div className="penki-balance-header">
        <button
          type="button"
          className="penki-balance-back"
          onClick={() => navigate(-1)}
          aria-label="Назад"
        >
          <img src="/chevron-back.png" alt="" width={24} height={24} />
        </button>
        <h1 className="penki-balance-heading">Баланс и транзакции</h1>
        <span className="penki-balance-header-spacer" />
      </div>

      <section className="penki-balance-card">
        <span className="penki-balance-label">Баланс УЙМ</span>
        <span className="penki-balance-value">{formatPenki(balance)}</span>
        <img src="/uym-badge.png" alt="" className="penki-balance-art" />
      </section>

      {loading ? (
        <div className="penki-balance-loading">
          <div className="catalog-spinner" />
        </div>
      ) : (
        <div className="penki-tx-sheet">
          {groups.length === 0 ? (
            <p className="penki-tx-empty">Пока нет операций</p>
          ) : (
            groups.map((group) => (
              <div className="penki-tx-group" key={group.key}>
                <div className="penki-tx-month">{formatMonthGroup(group.items[0].date)}</div>
                <div className="penki-tx-list">
                  {group.items.map((tx) => {
                    const isIncome = tx.type === 'income';
                    return (
                      <div className="penki-tx-item" key={tx.id}>
                        <div className="penki-tx-info">
                          <div className="penki-tx-icon" aria-hidden>
                            {isIncome ? '↓' : '↑'}
                          </div>
                          <div className="penki-tx-text">
                            <span className="penki-tx-title">{tx.description || (isIncome ? 'Начисление' : 'Списание')}</span>
                            <span className="penki-tx-date">{formatPenkiTransactionDate(tx.date)}</span>
                          </div>
                        </div>
                        <div className="penki-tx-amount">
                          {isIncome ? '+' : '−'}
                          {formatPenki(tx.amount)} УЙМ
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
          {page < lastPage && (
            <button
              type="button"
              className="button--secondary button--full"
              disabled={loadingMore}
              onClick={() => void load(page + 1, true)}
            >
              {loadingMore ? 'Загрузка...' : 'Показать ещё'}
            </button>
          )}
        </div>
      )}
    </div>
  );
};
